import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Send, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { formatDate } from "../../lib/utils-app";
import { MARK_STYLE, errMsg } from "./AttendanceTab";

const OPTIONS = [["P", "Present"], ["HD", "Half day"], ["L", "Leave"], ["A", "Absent"]];
const LABEL = { P: "Present", HD: "Half day", L: "Leave", A: "Absent" };
const STATE = { pending: "bg-amber-50 text-amber-800", approved: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700" };

export function RequestCorrectionDialog({ req, onClose, onSent }) {
  const [s, setS] = useState("P");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (req) { setS("P"); setWhy(""); } }, [req]);
  if (!req) return null;
  const send = async () => {
    setBusy(true);
    try { await api.post("/hr/attendance/requests", { date: req.day, status: s, reason: why }); toast.success("Request sent to HR"); onSent(); onClose(); }
    catch (e) { toast.error(errMsg(e, "Could not send")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm w-[calc(100vw-2rem)] sm:w-full" data-testid="att-request-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Request a correction</DialogTitle>
          <DialogDescription>{formatDate(req.day)} · now shown as <b>{req.current || "blank"}</b>. HR will review it, and it updates once approved.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">{OPTIONS.map(([k, l]) => <button key={k} type="button" onClick={() => setS(k)} className={`h-10 rounded-xl text-sm font-bold transition-all ${s === k ? "ring-2 ring-slate-900 " : ""}${MARK_STYLE[k]}`} data-testid={`att-request-${k}`}>{l}</button>)}</div>
          <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Reason, e.g. Was at the Palace Grounds site, forgot to clock in on Jibble" data-testid="att-request-reason" />
          <Button onClick={send} disabled={busy || !why.trim()} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="att-request-send"><Send className="h-4 w-4 mr-1.5" />Send to HR</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CorrectionsPanel({ isHr, refreshKey, onDecided }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/hr/attendance/requests", { params: isHr ? { state: "pending" } : {} }).then(({ data }) => setRows(data.filter((r) => r.state !== "replaced"))).catch(() => {}), [isHr]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const decide = async (r, approve) => {
    const note = approve ? "" : window.prompt("Reason for rejecting (optional)") ?? null;
    if (note === null) return;
    try { await api.post(`/hr/attendance/requests/${r.id}/decide`, { approve, note }); toast.success(approve ? "Approved, attendance updated" : "Rejected"); load(); onDecided?.(); }
    catch (e) { toast.error(errMsg(e, "Could not update")); }
  };
  const shown = isHr ? rows : rows.slice(0, 8);
  if (!shown.length) return null;
  return (
    <div className="bg-white border border-amber-200 rounded-xl" data-testid="att-requests">
      <div className="px-4 py-2.5 border-b border-amber-100 bg-amber-50/60 rounded-t-xl text-sm font-semibold text-amber-900 flex items-center gap-2"><Clock className="h-4 w-4" />{isHr ? `Correction requests to review (${rows.length})` : "My correction requests"}</div>
      <div className="divide-y divide-slate-100">
        {shown.map((r) => (
          <div key={r.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`att-request-row-${r.id}`}>
            <div className="flex-1 min-w-0 text-sm">
              <b>{isHr ? r.name : formatDate(r.date)}</b>{isHr && <> · {formatDate(r.date)}</>} → <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${MARK_STYLE[r.status]}`}>{LABEL[r.status]}</span>
              <div className="text-xs text-slate-500 truncate">{r.reason}{r.note ? ` · HR: ${r.note}` : ""}</div>
            </div>
            {isHr ? (
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => decide(r, true)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`att-request-approve-${r.id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
                <Button size="sm" variant="outline" onClick={() => decide(r, false)} className="h-9 text-red-700" data-testid={`att-request-reject-${r.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
              </div>
            ) : <span className={`self-start sm:self-auto text-[11px] font-bold rounded-full px-2 py-0.5 capitalize ${STATE[r.state]}`}>{r.state}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
